import { io, Socket } from 'socket.io-client';
import type {
  ArenaHalfExtent,
  ArenaSnapshot,
  ClientToServerEvents,
  InputCommand,
  MatchMode,
  ServerToClientEvents,
  SortieSettlement,
  TankState,
} from '@shared/protocol';
import type { TankSpec } from '@shared/tank-spec';

export interface JoinedPayload {
  playerId: string;
  nickname: string;
  spawn: TankState;
  tickRate: number;
  arenaHalfExtent: ArenaHalfExtent;
  /**
   * The arena actually joined, which is not always the one asked for: a room
   * reaped between the list being drawn and the socket connecting puts the
   * player in quick match instead.
   */
  arenaId: string;
  arenaName: string;
  /**
   * The map that arena is running.
   *
   * Not a hint and not decoration: every building drawn and every collision the
   * browser resolves comes from `mapById(mapId)`. A client that assumed the map
   * would predict its own movement against different geometry than the server
   * stepped it with — a small permanent error near every wall, under the snap
   * threshold, and therefore invisible except as a drift that never settles.
   */
  mapId: string;
  /** Which rules the arena runs. The client must not assume this either. */
  mode: MatchMode;
  mapName: string;
  /**
   * The loadout the server resolved for this session. Prediction — movement,
   * reload and shell flight alike — has to use the same numbers the server
   * does, so it is told to us rather than chosen here.
   */
  spec: TankSpec;
}

export interface ConnectionHandlers {
  onJoined: (payload: JoinedPayload) => void;
  onSnapshot: (snapshot: ArenaSnapshot) => void;
  onPlayerLeft: (playerId: string) => void;
  onRejected: (reason: string) => void;
  onDisconnected: () => void;
}

/**
 * Socket wrapper plus clock synchronisation.
 *
 * Interpolating remote tanks needs a shared timeline, and the browser clock is
 * not it — it can be minutes off. So we estimate the offset from the server
 * clock with a ping/pong probe and express render times on the server's
 * timeline instead.
 */
/**
 * How long to wait for the server to say what the sortie paid.
 *
 * Generous enough for a payout that writes several rows in one transaction, short
 * enough that a player who pressed Leave is never held by a reply that is not
 * coming. Past it the reward window is simply skipped — the money is banked either
 * way, because the server does that on its own schedule rather than on ours.
 */
const SETTLE_TIMEOUT_MS = 4000;

export class Connection {
  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  private pingTimer: number | null = null;

  /** serverTime − clientTime, in ms. */
  private clockOffset = 0;
  private rtt = 0;
  private sampled = false;

  /**
   * The last `joined` payload, kept so the scene can read the loadout the
   * server assigned without every caller having to thread it through.
   */
  private joined: JoinedPayload | null = null;

  /**
   * Opens the battle socket and joins.
   *
   * `arenaId` is optional and stays that way: omitted, the server puts the
   * player in quick match, which is exactly what the Deploy button has always
   * done. That is the whole of what single-player-with-bots had to change.
   *
   * `mapId` is a preference and only applies when no arena is named — an
   * arena's map is fixed at construction, so naming a room names its map.
   * Whatever happens, the answer comes back in `joined` and everything downstream
   * reads it from there.
   */
  connect(
    nickname: string,
    clientId: string,
    handlers: ConnectionHandlers,
    arenaId?: string,
    mapId?: string,
    /** Which rules to play by. Only consulted when no arena was named. */
    mode?: MatchMode,
  ): void {
    this.disconnect();

    // Same-origin in dev: Vite proxies /ws through to the API.
    const socket: Socket<ServerToClientEvents, ClientToServerEvents> = io({
      path: '/ws',
      transports: ['websocket'],
    });
    this.socket = socket;

    socket.on('connect', () => {
      socket.emit('join', {
        nickname,
        clientId,
        ...(arenaId ? { arenaId } : {}),
        ...(mapId ? { mapId } : {}),
        ...(mode ? { mode } : {}),
      });
      this.probe();
      this.pingTimer = window.setInterval(() => this.probe(), 2000);
    });

    socket.on('joined', (payload) => {
      this.joined = payload;
      handlers.onJoined(payload);
    });
    socket.on('snapshot', handlers.onSnapshot);
    socket.on('playerLeft', ({ playerId }) => handlers.onPlayerLeft(playerId));
    socket.on('rejected', ({ reason }) => handlers.onRejected(reason));
    socket.on('disconnect', () => handlers.onDisconnected());

    socket.on('pong', ({ sentAt, serverTime }) => {
      const now = Date.now();
      const rtt = now - sentAt;
      // Assume a symmetric path: the server's clock at `now` is serverTime plus
      // half the round trip.
      const offset = serverTime + rtt / 2 - now;

      if (!this.sampled) {
        this.clockOffset = offset;
        this.rtt = rtt;
        this.sampled = true;
      } else {
        // Low-pass the estimate so one delayed packet doesn't yank the timeline.
        this.clockOffset += (offset - this.clockOffset) * 0.2;
        this.rtt += (rtt - this.rtt) * 0.2;
      }
    });
  }

  private probe(): void {
    this.socket?.emit('ping', { sentAt: Date.now() });
  }

  sendInput(input: InputCommand): void {
    this.socket?.emit('input', input);
  }

  /** Best estimate of the server's clock right now, in ms. */
  serverNow(): number {
    return Date.now() + this.clockOffset;
  }

  get latencyMs(): number {
    return Math.round(this.rtt);
  }

  /** The loadout the server assigned this session, once `joined` has arrived. */
  get spec(): TankSpec | null {
    return this.joined?.spec ?? null;
  }

  /** The arena we are actually in — the server's answer, not what we asked for. */
  get arenaName(): string | null {
    return this.joined?.arenaName ?? null;
  }

  get arenaId(): string | null {
    return this.joined?.arenaId ?? null;
  }

  /** The map the server put us on. The client never decides this. */
  get mode(): MatchMode | null {
    return this.joined?.mode ?? null;
  }

  get mapId(): string | null {
    return this.joined?.mapId ?? null;
  }

  get mapName(): string | null {
    return this.joined?.mapName ?? null;
  }

  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Leaves the battle on purpose, and answers with what the sortie paid.
   *
   * The payout is banked *after* the arena releases the seat, so simply dropping
   * the socket — which is what this used to do, and what a crash still does —
   * leaves nobody to tell. So a deliberate exit sends `leave` and waits for the
   * server's own figures; the client never computes a payout.
   *
   * **Bounded, and null on every unhappy path.** A reply that never comes must not
   * strand a player in a battle they asked to leave, which is the same reasoning
   * `LOADING_TIMEOUT_MS` follows on the way in: the window is a courtesy and
   * leaving is not. The listener is removed on both paths, since `Connection` is
   * reused across sorties and a stale one-shot would resolve the *next* leave with
   * the last one's figures.
   */
  async leaveAndSettle(): Promise<SortieSettlement | null> {
    const socket = this.socket;
    if (!socket?.connected) return null;
    return new Promise((resolve) => {
      const settle = (payload: SortieSettlement | null): void => {
        window.clearTimeout(timer);
        socket.off('sortieClosed', settle);
        resolve(payload ?? null);
      };
      const timer = window.setTimeout(() => settle(null), SETTLE_TIMEOUT_MS);
      socket.on('sortieClosed', settle);
      socket.emit('leave');
    });
  }

  disconnect(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
    this.sampled = false;
    this.joined = null;
  }
}
